import type { PayloadAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../store';
import { DefaultCalendarSetPresenzeModo } from '../pages/Default/store';
import { StudioCalendarSetPresenzeModo } from '../pages/Studio/store';
import type {
  CalendarioConPresenze,
  CalendarioId,
  PresenzeModo,
} from '../utility_objects/preferenzeCalendario';

// Calendari senza fascia: sempre nessuna, senza leggere lo store
const NESSUNA = (): PresenzeModo => 'nessuna';

// Un selettore per calendario, creato una volta sola: identità stabile per
// useSelector
const SELETTORI: Record<CalendarioId, (state: RootState) => PresenzeModo> = {
  negozio: (state) => state.bluBookingDefaultCalendar.presenzeModo,
  studio: (state) => state.bluBookingStudioCalendar.presenzeModo,
  personale: NESSUNA,
  utente: NESSUNA,
};

const AZIONI: Record<
  CalendarioConPresenze,
  (modo: PresenzeModo) => PayloadAction<PresenzeModo>
> = {
  negozio: DefaultCalendarSetPresenzeModo,
  studio: StudioCalendarSetPresenzeModo,
};

/**
 * Selettore della modalità della fascia Presenze del calendario; per chi deve
 * solo leggerla (il pallino del pulsante Visualizzazione). Personale e
 * giornaliero Utente non hanno la fascia: sempre 'nessuna'.
 */
export const selectPresenzeModo = (
  calendario: CalendarioId
): ((state: RootState) => PresenzeModo) => SELETTORI[calendario];

/**
 * Modalità della fascia Presenze (nessuna, «Presenze», «Totali»), la riga
 * Presenze del riquadro Visualizzazione. Scelta del calendario, solo dove c'è
 * la fascia (agenda negozio, giornaliero Studio): vive nello store del
 * calendario ed è ricordata per PC nel suo cookie (COOKIE_PRESENZE_MODO:
 * defaultCalendarPresenzeModo, studioCalendarPresenzeModo; assente =
 * nessuna).
 */
export const usePresenzeModo = (
  calendario: CalendarioConPresenze
): {
  modo: PresenzeModo;
  imposta: (modo: PresenzeModo) => void;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const modo = useSelector(selectPresenzeModo(calendario));
  const imposta = useCallback(
    (nuovo: PresenzeModo) => dispatch(AZIONI[calendario](nuovo)),
    [dispatch, calendario]
  );
  return { modo, imposta };
};
